/**
 * Fullscreen helpers for wall displays and kiosk tablets.
 *
 * Selecting a screen requests fullscreen on the tap gesture. That call used to
 * be `document.documentElement.requestFullscreen?.().catch(() => {})`, which
 * fails silently in two ways:
 *   - iPadOS/older Safari expose only `webkitRequestFullscreen`, so the
 *     optional call short-circuits and nothing happens at all;
 *   - a genuine rejection (no user activation, blocked by policy) is swallowed,
 *     leaving no clue why the wall never went fullscreen.
 *
 * These helpers try the prefixed variants and report failures instead.
 */

export function isFullscreen() {
  if (typeof document === 'undefined') return false;
  return !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.msFullscreenElement
  );
}

export function isFullscreenSupported() {
  if (typeof document === 'undefined') return false;
  const el = document.documentElement;
  return !!(
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.msRequestFullscreen
  );
}

/**
 * Request fullscreen. Must be called synchronously from a user gesture.
 * @returns {Promise<boolean>} whether fullscreen was entered
 */
export async function enterFullscreen(element) {
  if (typeof document === 'undefined') return false;
  const el = element || document.documentElement;
  const request =
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.msRequestFullscreen;

  if (!request) {
    console.warn('[fullscreen] not supported on this browser (no requestFullscreen variant) — iOS Safari only allows fullscreen video');
    return false;
  }

  try {
    await request.call(el);
    return true;
  } catch (err) {
    // Surfaced, not swallowed: "not supported", "gesture required" and policy
    // blocks are indistinguishable otherwise.
    console.warn('[fullscreen] request rejected:', err?.name || '', err?.message || err);
    return false;
  }
}

export async function exitFullscreen() {
  if (typeof document === 'undefined') return false;
  const exit =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.msExitFullscreen;
  if (!exit) return false;
  try {
    await exit.call(document);
    return true;
  } catch (err) {
    console.warn('[fullscreen] exit failed:', err?.message || err);
    return false;
  }
}

/** Subscribe to fullscreen changes across prefixes. Returns an unsubscribe fn. */
export function onFullscreenChange(handler) {
  const events = ['fullscreenchange', 'webkitfullscreenchange', 'MSFullscreenChange'];
  events.forEach(e => document.addEventListener(e, handler));
  return () => events.forEach(e => document.removeEventListener(e, handler));
}
